import Papa from "papaparse";

// Shared CSV export for the admin portal list screens.
//
// The report screens each carry their own byte-identical copy of an
// exportToCSV helper (ReportUtil, OrderHistoryUtil, ReportCloverOrderUtil,
// ReportPaymentIntentUtil, ReportUserPurchaseUtil, ReportUserWalletUtil). New
// screens use this one instead of adding a seventh copy. The existing six are
// deliberately left alone - nine money and reporting screens depend on them and
// re-pointing those is a separate change, not a rider on a feature.
//
// Three things this does that the copied helper does not:
//
//   1. Writes a UTF-8 BOM. Without it Excel on Windows reads the file as
//      Latin-1, so a member named Bronwyn Ní Dhomhnaill arrives mangled.
//   2. Neutralises formula injection. A cell beginning = + - @ is executed by
//      Excel and Sheets when the file is opened. Member-supplied fields end up
//      in these exports, so a name or bio is enough to carry one.
//   3. Offers asText() for identifiers, because Excel converts 00013 to 13.

const FORMULA_LEAD = /^[=+\-@\t\r]/;
const ALREADY_TEXT = /^="(?:[^"]|"")*"$/;

// Mark a value so Excel keeps it as text rather than parsing it as a number.
//
// Member Id is the reason this exists: the portal shows 00013 and 001, and
// opening a plain CSV in Excel turns those into 13 and 1, which no longer match
// what staff see on screen or what members quote on the phone.
//
// The ="..." form is understood by Excel, Numbers and Google Sheets. Inner
// quotes are doubled so a value containing one cannot break out of the wrapper.
export function asText(value) {
    if (value === null || value === undefined || value === "") return "";
    return `="${String(value).replace(/"/g, '""')}"`;
}

// Everything that is not a deliberate asText() cell gets checked. A leading
// apostrophe is the conventional escape: spreadsheets strip it on display and
// treat the rest as literal text, so the value still reads correctly.
function neutralizeFormula(value) {
    if (typeof value !== "string" || value === "") return value;
    if (ALREADY_TEXT.test(value)) return value;
    return FORMULA_LEAD.test(value) ? `'${value}` : value;
}

function sanitizeRow(row) {
    const out = {};
    Object.keys(row).forEach((key) => {
        const v = row[key];
        out[key] = v === null || v === undefined ? "" : neutralizeFormula(v);
    });
    return out;
}

// "members-2026-11-01.csv" - the screen name plus the day it was taken, so a
// folder of exports stays readable and two exports on different days do not
// overwrite each other.
export function csvFileName(screenName) {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const slug = String(screenName || "export")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return `${slug || "export"}-${stamp}`;
}

// rows: array of plain objects, keys become the header row in insertion order.
// Returns the number of rows written so the caller can report and audit it.
export function exportToCsv(rows, fileName) {
    const data = Array.isArray(rows) ? rows : [];
    if (data.length === 0) return 0;

    const csv = Papa.unparse(data.map(sanitizeRow));
    // ﻿ is the BOM. It must lead the blob, not the string passed to Papa,
    // or it becomes part of the first column heading.
    const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Chrome keeps the object alive until the tab closes otherwise, and these
    // blobs are the whole member base.
    URL.revokeObjectURL(url);

    return data.length;
}
